import React, { useState, useEffect } from 'react';

function ToolTimingChallenge({ questions, xpReward, coinReward, timeLimit, onComplete, difficulty = 'medium' }) {
  // Difficulty settings
  const DIFFICULTY_SETTINGS = {
    easy:   { time: timeLimit + 5, xp: 1, coin: 1 },
    medium: { time: timeLimit,     xp: 1.2, coin: 1.2 },
    hard:   { time: Math.max(5, timeLimit - 3), xp: 1.5, coin: 1.5 },
  };
  const settings = DIFFICULTY_SETTINGS[difficulty] || DIFFICULTY_SETTINGS['medium'];

  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState(null); // 'correct' | 'wrong' | 'timeout'
  const [timer, setTimer] = useState(settings.time);
  const [score, setScore] = useState(0);
  const [coins, setCoins] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [streak, setStreak] = useState(0);
  const [maxStreak, setMaxStreak] = useState(0);
  const [bonus, setBonus] = useState(0);
  const [anim, setAnim] = useState('');

  useEffect(() => {
    if (showResult) return;
    if (timer === 0) {
      handleAnswer(null, true);
      return;
    }
    const t = setTimeout(() => setTimer(timer - 1), 1000);
    return () => clearTimeout(t);
  }, [timer, showResult]);

  // Animate feedback
  useEffect(() => {
    if (feedback === 'correct') setAnim('flash-correct');
    else if (feedback === 'wrong' || feedback === 'timeout') setAnim('flash-wrong');
    else setAnim('');
  }, [feedback]);

  const handleAnswer = (option, isTimeout = false) => {
    if (selected !== null) return;
    setSelected(option);
    const correct = questions[current].correctAnswer;
    let nextStreak = streak;
    let streakBonus = 0;
    if (!isTimeout && option === correct) {
      setFeedback('correct');
      setScore(s => s + 1);
      setCoins(c => c + Math.round(coinReward * settings.coin));
      nextStreak = streak + 1;
      setStreak(nextStreak);
      setMaxStreak(ms => Math.max(ms, nextStreak));
      if (nextStreak > 1) {
        streakBonus = nextStreak * 2; // Bonus increases with streak
        setBonus(streakBonus);
      } else {
        setBonus(0);
      }
    } else {
      setFeedback(isTimeout ? 'timeout' : 'wrong');
      setStreak(0);
      setBonus(0);
    }
    setTimeout(() => {
      setFeedback(null);
      setSelected(null);
      setTimer(settings.time - (difficulty === 'hard' ? current + 1 : 0)); // Hard: timer gets shorter
      if (current + 1 < questions.length) {
        setCurrent(c => c + 1);
      } else {
        setShowResult(true);
        const finalScore = score + (!isTimeout && option === correct ? 1 : 0);
        const totalBonus = (maxStreak > 1 ? maxStreak * 2 : 0);
        setTimeout(() => onComplete({
          xp: Math.round(xpReward * settings.xp * finalScore + totalBonus),
          coins: Math.round(coinReward * settings.coin * finalScore + totalBonus),
          streak: maxStreak
        }), 1200);
      }
    }, 900);
  };

  if (showResult) {
    return (
      <div style={{ textAlign: 'center', padding: 24 }}>
        <h3 style={{ color: '#22c55e', fontWeight: 800, fontSize: 24, marginBottom: 12 }}>Challenge Complete!</h3>
        <div style={{ fontSize: 18, marginBottom: 10 }}>You earned:</div>
        <div style={{ fontWeight: 700, fontSize: 22, color: '#22c55e' }}>{Math.round(xpReward * settings.xp * score + (maxStreak > 1 ? maxStreak * 2 : 0))} XP</div>
        <div style={{ fontWeight: 700, fontSize: 22, color: '#fbbf24' }}>{Math.round(coinReward * settings.coin * score + (maxStreak > 1 ? maxStreak * 2 : 0))} Coins</div>
        <div style={{ marginTop: 18, color: '#555' }}>Correct Answers: {score} / {questions.length}</div>
        <div style={{ marginTop: 8, color: '#0ea5e9', fontWeight: 600 }}>Max Streak: {maxStreak}</div>
        <button className="gogig-card-btn" style={{ marginTop: 24 }} onClick={() => onComplete({ xp: Math.round(xpReward * settings.xp * score + (maxStreak > 1 ? maxStreak * 2 : 0)), coins: Math.round(coinReward * settings.coin * score + (maxStreak > 1 ? maxStreak * 2 : 0)), streak: maxStreak })}>Continue</button>
      </div>
    );
  }

  const q = questions[current];
  const percent = ((settings.time - timer) / settings.time) * 100;
  const difficultyColors = { easy: '#a7f3d0', medium: '#fde68a', hard: '#fecaca' };
  const difficultyLabels = { easy: 'Easy', medium: 'Medium', hard: 'Hard' };

  return (
    <div style={{ padding: 12, minWidth: 280, maxWidth: 340, textAlign: 'center', position: 'relative' }} className={anim}>
      <div style={{ position: 'absolute', top: 8, right: 8, background: difficultyColors[difficulty], color: '#222', borderRadius: 8, padding: '2px 10px', fontWeight: 700, fontSize: 13, letterSpacing: 1 }}>{difficultyLabels[difficulty]}</div>
      <div style={{ marginBottom: 10, fontWeight: 600, color: '#222' }}>Question {current + 1} / {questions.length}</div>
      <div style={{ height: 8, background: '#e5e7eb', borderRadius: 6, marginBottom: 18, overflow: 'hidden' }}>
        <div style={{ width: `${100 - (timer / settings.time) * 100}%`, height: '100%', background: timer > 3 ? '#22c55e' : '#ef4444', transition: 'width 0.2s' }} />
      </div>
      <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 16 }}>{q.question}</div>
      {q.image && <img src={q.image} alt="question visual" style={{ maxWidth: '100%', marginBottom: 12, borderRadius: 8 }} />}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {q.options.map(opt => (
          <button
            key={opt}
            className={`gogig-card-btn${selected === opt ? (opt === q.correctAnswer ? ' correct' : ' wrong') : '-outline'}`}
            style={{ fontSize: 16, border: feedback && opt === q.correctAnswer && feedback === 'correct' ? '2px solid #22c55e' : feedback && selected === opt && feedback === 'wrong' ? '2px solid #ef4444' : undefined, background: selected === opt ? (opt === q.correctAnswer ? '#bbf7d0' : '#fecaca') : undefined, color: selected === opt ? '#222' : undefined, transition: 'all 0.2s' }}
            onClick={() => handleAnswer(opt)}
            disabled={selected !== null}
          >
            {opt}
            {feedback && selected === opt && feedback === 'correct' && <span style={{ marginLeft: 8, color: '#22c55e', fontWeight: 700 }}>✅</span>}
            {feedback && selected === opt && feedback === 'wrong' && <span style={{ marginLeft: 8, color: '#ef4444', fontWeight: 700 }}>❌</span>}
            {feedback && feedback === 'timeout' && <span style={{ marginLeft: 8, color: '#ef4444', fontWeight: 700 }}>⏰</span>}
          </button>
        ))}
      </div>
      <div style={{ marginTop: 18, color: '#888', fontSize: 15 }}>Time left: {timer}s</div>
      <div style={{ marginTop: 8, color: '#0ea5e9', fontWeight: 600 }}>Streak: {streak > 1 ? streak : 0} {bonus > 0 && <span style={{ color: '#f59e42', marginLeft: 6 }}>+{bonus} Bonus!</span>}</div>
    </div>
  );
}

// Add some basic flash animation styles
const style = document.createElement('style');
style.innerHTML = `
.flash-correct { animation: flash-correct 0.5s; }
@keyframes flash-correct { 0% { background: #bbf7d0; } 100% { background: none; } }
.flash-wrong { animation: flash-wrong 0.5s; }
@keyframes flash-wrong { 0% { background: #fecaca; } 100% { background: none; } }
`;
document.head.appendChild(style);

export default ToolTimingChallenge; 